import { PartialObject } from "../object/object";

// todo: use object spread instead of `Object.assign` when fixed with generics https://github.com/Microsoft/TypeScript/issues/10727

/**
 * Updates the state by merging the changes and return a new state value.
 *
 * @param state original state reference.
 * @param changes changes to be merged.
 */
export function updateState<TState>(state: TState, changes: PartialObject<TState>): TState {
	return Object.assign({}, state, changes) as TState;
}

/**
 * Updates a state property by merging the changes into the state property and it also returns a new state.
 */
export function updateStateItem<TState, TStateItem>(params: {
	state: TState;
	propertyName: Extract<keyof TState, string | number>;
	item: TStateItem;
	changes: PartialObject<TStateItem>;
}): TState {
	const newSubState: TStateItem = Object.assign({}, params.item, params.changes);
	return Object.assign({}, params.state, { [params.propertyName]: newSubState });
}

/**
 * Updates (add/update) an item within the state (which is a dictionary/map state)
 * with the `key` provided, by finding, merging the changes and returns a new state.
 *
 * @param key key to find and update.
 * @param state original state reference.
 * @param changes item changes to be added/updated.
 * @returns returns a new state with changes applied.
 */
export function updateMapState<TState extends Record<string, TStateItem>, TStateItem>(
	key: string | number,
	state: TState,
	changes: PartialObject<TStateItem>
): TState {
	return Object.assign({}, state, {
		[key]: Object.assign({}, state[key], changes),
	});
}

/**
 * Updates (add/update) a collection of items within the state (which is a dictionary/map state) using the
 * property `keySelector` (default `id`) as the id within the provided item, leaving certain properties (`exceptSetProperties`)
 * as they were before if they had a value
 * Finding, merging each individual item changes and returns a new state.
 *
 * @param state original state reference.
 * @param changes list of changes to be added/updated.
 * @param [keySelector="id"] Property name used as the key within the model.
 * @param exceptSetProperties list of properties which should be updated if they do not have values yet
 * @returns returns a new state with the changes applied.
 */
export function updateAllMapState<TState extends Record<string, TStateItem>, TStateItem extends Record<string, any>>(
	state: TState,
	changes: (Partial<TStateItem> | null | undefined)[],
	keySelector: keyof TStateItem | "id" = "id",
	exceptSetProperties?: (keyof TStateItem)[]
): TState {
	const newState: TState = Object.assign({}, state);

	for (const item of changes) {
		if (!item) {
			continue;
		}
		const key = item[keySelector] as keyof TState;
		if (!key) {
			continue;
		}

		const newItem = Object.assign({}, item);

		const newStateValue = newState[key];
		if (newStateValue && exceptSetProperties) {
			for (const property of exceptSetProperties) {
				if (newStateValue.hasOwnProperty(property)) {
					newItem[property] = newStateValue[property as any];
				}
			}
		}

		newState[key] = Object.assign({}, newState[key], newItem);
	}
	return newState;
}
